/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace gamma
*/
var gamma = {};

/**
* @name cdf
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/cdf}
*/
import cdf from './../../../../base/dists/gamma/cdf';
setReadOnly( gamma, 'cdf', cdf );

/**
* @name Gamma
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/ctor}
*/
import Gamma from './../../../../base/dists/gamma/ctor';
setReadOnly( gamma, 'Gamma', Gamma );

/**
* @name entropy
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/entropy}
*/
import entropy from './../../../../base/dists/gamma/entropy';
setReadOnly( gamma, 'entropy', entropy );

/**
* @name kurtosis
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/kurtosis}
*/
import kurtosis from './../../../../base/dists/gamma/kurtosis';
setReadOnly( gamma, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/logcdf}
*/
import logcdf from './../../../../base/dists/gamma/logcdf';
setReadOnly( gamma, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/logpdf}
*/
import logpdf from './../../../../base/dists/gamma/logpdf';
setReadOnly( gamma, 'logpdf', logpdf );

/**
* @name mean
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/mean}
*/
import mean from './../../../../base/dists/gamma/mean';
setReadOnly( gamma, 'mean', mean );

/**
* @name mgf
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/mgf}
*/
import mgf from './../../../../base/dists/gamma/mgf';
setReadOnly( gamma, 'mgf', mgf );

/**
* @name mode
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/mode}
*/
import mode from './../../../../base/dists/gamma/mode';
setReadOnly( gamma, 'mode', mode );

/**
* @name pdf
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/pdf}
*/
import pdf from './../../../../base/dists/gamma/pdf';
setReadOnly( gamma, 'pdf', pdf );

/**
* @name quantile
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/quantile}
*/
import quantile from './../../../../base/dists/gamma/quantile';
setReadOnly( gamma, 'quantile', quantile );

/**
* @name skewness
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/skewness}
*/
import skewness from './../../../../base/dists/gamma/skewness';
setReadOnly( gamma, 'skewness', skewness );

/**
* @name stdev
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/stdev}
*/
import stdev from './../../../../base/dists/gamma/stdev';
setReadOnly( gamma, 'stdev', stdev );

/**
* @name variance
* @memberof gamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gamma/variance}
*/
import variance from './../../../../base/dists/gamma/variance';
setReadOnly( gamma, 'variance', variance );


// EXPORTS //

export default gamma;
